import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { Header, getUserNotificationCount } from "../../components/Header";
import { HeaderRoleBadge } from "../../components/HeaderRoleBadge";
import { DateRange } from "../../components/DateRange";
import { SessionOptions } from "../../components/Grouplist";
import { Sessions } from "../../components/Sessions";
import { TodaySessions } from "../../components/TodaySessions";
import {
  homemetric,
  homepageadmin,
  profile,
} from "../../components/funtionsBD/Mainservice";
import defaultAppLogo from "../../assets/smarttt-default-logo.png";
import { resolveClubImageUrl } from "../../utils/resolveClubImageUrl";
import { openSwitchClubDialog } from "../../utils/switchActiveClubClient";
import { Metricsp } from "../../components/MetricsPLayer";
import { MetricsAdminHome } from "../../components/MetricsAdminHome";
import { MetricsCoachHome } from "../../components/MetricsCoachHome";
import { MetricsPlayerYear } from "../../components/MetricsPlayerYear";
import { PlayerLastSessionCard } from "../../components/PlayerLastSessionCard";
import { PlayerNewTrainingSessionDialog } from "../../components/PlayerNewTrainingSessionDialog";
import { requestLiveFullscreen } from "../../hooks/useLiveFullscreen";
import { persistLiveTrainingSession } from "../../hooks/useLiveTrainingRedirect";
import { useBrand } from "../../context/BrandContext";
import {
  AutoevalBanner,
  shouldFetchPendingAutoevaluations,
} from "../../components/AutoevalBanner";
import {
  HOME_ACTIVE_ROLE_KEY,
  HOME_ROLE_PREVIEW_KEY,
  getHomeRolesFromUser,
  getHomeRoleSwitcherOptions,
  isPreviewHomeRole,
  mapHomeRoleToUserType,
  readActiveHomeRole,
  readStoredUser,
} from "../../utils/homeRole";
import { LiveTrainingReturnFab } from "../../components/LiveTrainingReturnFab";
import { normalizeStoredGroupInfo } from "../../utils/liveTrainingGroupInfo";
import { useCoachLiveTrainingAutoEnter } from "../../hooks/useCoachLiveTrainingAutoEnter";
import { usePermissions } from "../../context/Permissioncontext";
import { openDigitalTwin } from "../../components/HomeDailyFocus";
import { fetchSmartProfile } from "../../components/funtionsBD/digipingSmartService";
import { isUiTourSettledForRole } from "../../tour";
import { OPEN_PLAYER_NEW_SESSION_EVENT, UI_TOUR_SETTLED_EVENT, CLOSE_TOUR_TRANSIENT_UI_EVENT } from "../../tour/tourEvents";
import { useTourDemoActive } from "../../tour/useTourDemoActive";
import { demoDashboardCounts, demoHomeGroups, isTourDemoId } from "../../tour/tourDemoFixtures";
import {
  clubNeedsQuickSetup,
  openClubSetupTwin,
} from "../../utils/digipingClubSetup";
import {
  currentAppLanguage,
  preferredAppLanguage,
  applyAppLanguage
} from "../../utils/appLanguages";
import { EmailVerificationBanner } from "../../components/EmailVerificationBanner";
import { NewsletterHomeCard } from "../../components/newsletter/NewsletterHomeCard";
import { mergeProfilePreservingActiveClub } from "../../utils/mergeProfilePreservingActiveClub";
import "../../components/Home.css";
import "../../components/Loading/Loader.css";

function readStoredGroupFromLocalStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem("grpinf") || "null");
    if (!raw) return null;
    if (raw.groupDetails?.gr_id) return raw;
    const healed = normalizeStoredGroupInfo(raw);
    if (healed) {
      localStorage.setItem("grpinf", JSON.stringify(healed));
      return healed;
    }
    return null;
  } catch {
    return null;
  }
}

/** Stable empty list so `coachHomeGroups` keeps the same reference while home data is loading. */
const EMPTY_HOME_GROUP_LIST = [];

function homeGroupRowId(g) {
  return g?.groupDetails?.gr_id || g?.gr_id || null;
}

function isAutoHomeGroup(g) {
  return (
    (g?.groupDetails?.gr_traning_session_type || g?.gr_traning_session_type) ===
    "auto"
  );
}

/** Pick the group to keep selected after a homePage payload, matching prior Home behavior. */
function selectHomeGroupFromPayload(list, currentSelected) {
  const selectable = list.filter((g) => homeGroupRowId(g) && !isAutoHomeGroup(g));
  const selId = homeGroupRowId(currentSelected);
  const matchInList = (id) =>
    id
      ? list.find((i) => String(homeGroupRowId(i) || "") === String(id)) || null
      : null;
  const matchedSel = matchInList(selId);
  if (list.length === 0) {
    return { selected: undefined, persist: false };
  }
  if (matchedSel && !isAutoHomeGroup(matchedSel)) {
    return { selected: matchedSel, persist: false };
  }
  let storedId = null;
  try {
    const stored = JSON.parse(localStorage.getItem("grpinf") || "null");
    storedId = homeGroupRowId(stored);
  } catch {
    storedId = null;
  }
  const matchedStored = matchInList(storedId);
  if (matchedStored && !isAutoHomeGroup(matchedStored)) {
    return { selected: matchedStored, persist: true };
  }
  if (selectable.length > 0) {
    const last = selectable[selectable.length - 1];
    return { selected: last, persist: true };
  }
  return { selected: undefined, persist: false };
}

/** Align i18n codes with backend attribute translations (gr.json → de, sp.json → es) */
function normalizeAppLang(lng) {
  const base = (lng || "en").split("-")[0].toLowerCase();
  if (base === "gr") return "de";
  if (base === "sp") return "es";
  return base;
}

function getDefaultCalendarDate() {
  return moment().startOf("day").toDate();
}

function isCalendarToday(date) {
  return Boolean(date) && moment(date).isSame(moment(), "day");
}

/**
 * Calendar + date-filtered session cards. Session list is part of the same
 * expandable section as the calendar (collapsed by default).
 */
function HomeCalendarSessionsSection({
  selectedDate,
  onDateChange,
  groupIds,
  tourAgenda = false,
  calendarExpanded,
  onCalendarExpandedChange,
  calendarClassName = "",
  calendarTourId,
  sessionsKey,
  sessionsProps,
}) {
  return (
    <div
      className={[
        "calendar-and-add-section",
        calendarExpanded ? "is-expanded" : "",
        calendarClassName,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className="calendar-wrapper"
        data-tour-id={calendarTourId}
      >
        <DateRange
          selectionRange={selectedDate}
          setSelectionRange={(date) => {
            onDateChange(date);
            onCalendarExpandedChange(true);
          }}
          groupIds={groupIds}
          tourAgenda={tourAgenda}
          expanded={calendarExpanded}
          onExpandedChange={onCalendarExpandedChange}
          collapseOnSelect={false}
        />
      </div>
      {/* Always mount Sessions so openAddSessionDialog (group card +) keeps working
          while the date-filtered list stays collapsed with the calendar. */}
      <div
        className="home-date-sessions-with-calendar"
        style={{
          display: calendarExpanded && selectedDate ? "block" : "none",
        }}
        aria-hidden={!(calendarExpanded && selectedDate)}
      >
        <Sessions
          key={sessionsKey}
          {...sessionsProps}
          selectedDate={selectedDate}
        />
      </div>
    </div>
  );
}

function digipingAutoPromptKey(kind, userId) {
  return `digiping:autoPrompt:${kind}:${userId}`;
}

/** Session-scoped: reopen on a later visit until the step is actually complete. */
function hasDigipingAutoPrompted(kind, userId) {
  if (!userId) return false;
  try {
    return sessionStorage.getItem(digipingAutoPromptKey(kind, userId)) === "1";
  } catch {
    return false;
  }
}

function markDigipingAutoPrompted(kind, userId) {
  if (!userId) return;
  try {
    sessionStorage.setItem(digipingAutoPromptKey(kind, userId), "1");
    // Clear legacy permanent flags so incomplete flows can reopen.
    localStorage.removeItem(digipingAutoPromptKey(kind, userId));
  } catch {
    /* ignore quota */
  }
}

export function CoachHome({ isActive = true }) {
  const [mdata, setmdata] = useState();
  const [grplist, setgrplist] = useState();
  const [homeLoading, setHomeLoading] = useState(true);
  const tourDemo = useTourDemoActive();
  const [selectedgrp, setselectedgrp] = useState(readStoredGroupFromLocalStorage);
  const selectedgrpRef = useRef(selectedgrp);
  selectedgrpRef.current = selectedgrp;
  const homePayloadRef = useRef(null);
  const displayUserRef = useRef(null);
  const roleChangePendingRef = useRef(false);
  const [selectionRange, setSelectionRange] = useState(getDefaultCalendarDate);
  const [selectedDate, setSelectedDate] = useState(getDefaultCalendarDate);
  const [finalizedPickDate, setFinalizedPickDate] = useState(getDefaultCalendarDate);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [finalizedCalendarExpanded, setFinalizedCalendarExpanded] =
    useState(false);
  /** Coach-assigned groups when home is viewed as admin (parallel to admin club group list). */
  const [adminCoachGroupList, setAdminCoachGroupList] = useState(null);

  const { t, i18n } = useTranslation();
  const { brand, refreshBrand } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [displayUser, setDisplayUser] = useState(() =>
    JSON.parse(localStorage.getItem("user") || "{}")
  );
  displayUserRef.current = displayUser;
  const [playerSessionOpen, setPlayerSessionOpen] = useState(false);
  const clubSetupPromptOpenedRef = useRef(false);
  const clubJoinPromptOpenedRef = useRef(false);
  const [tourSettled, setTourSettled] = useState(() =>
    isUiTourSettledForRole(readActiveHomeRole(JSON.parse(localStorage.getItem("user") || "{}")) )
  );

  useEffect(() => {
    const onTour = (event) => {
      const nextRole = event?.detail?.role || readActiveHomeRole(displayUserRef.current);
      if (event?.detail?.completed || event?.detail?.skipped) {
        setTourSettled(true);
        return;
      }
      setTourSettled(isUiTourSettledForRole(nextRole, displayUserRef.current?.user_id));
    };
    const onRole = () => {
      const nextRole = readActiveHomeRole(displayUserRef.current);
      setActiveHomeRole(nextRole);
      roleChangePendingRef.current = true;
      setTourSettled(isUiTourSettledForRole(nextRole, displayUserRef.current?.user_id));
    };
    window.addEventListener(UI_TOUR_SETTLED_EVENT, onTour);
    window.addEventListener("smarttt-home-role-changed", onRole);
    return () => {
      window.removeEventListener(UI_TOUR_SETTLED_EVENT, onTour);
      window.removeEventListener("smarttt-home-role-changed", onRole);
    };
  }, []);

  useEffect(() => {
    const openPlayerSession = () => setPlayerSessionOpen(true);
    const dismissTourDialogs = (event) => {
      if (event?.detail?.keep?.playerNewSession) return;
      setPlayerSessionOpen(false);
    };
    window.addEventListener(OPEN_PLAYER_NEW_SESSION_EVENT, openPlayerSession);
    window.addEventListener(CLOSE_TOUR_TRANSIENT_UI_EVENT, dismissTourDialogs);
    return () => {
      window.removeEventListener(OPEN_PLAYER_NEW_SESSION_EVENT, openPlayerSession);
      window.removeEventListener(CLOSE_TOUR_TRANSIENT_UI_EVENT, dismissTourDialogs);
    };
  }, []);

  useEffect(() => {
    const onProfilePatch = (event) => {
      const patch = event?.detail || {};
      if (!patch || typeof patch !== "object") return;
      setDisplayUser((prev) => {
        const next = { ...(prev || {}), ...patch };
        try {
          localStorage.setItem("user", JSON.stringify(next));
        } catch (_) {
          /* ignore */
        }
        return next;
      });
    };
    window.addEventListener("smarttt:user-profile-updated", onProfilePatch);
    return () => window.removeEventListener("smarttt:user-profile-updated", onProfilePatch);
  }, []);

  useEffect(() => {
    const onClubChanged = (event) => {
      const stored = readStoredUser() || {};
      const eventClubId = event?.detail?.club_id;
      const next = eventClubId ? { ...stored, club_id: eventClubId } : stored;
      setDisplayUser(next);
      setgrplist(undefined);
      setmdata(undefined);
      setselectedgrp(undefined);
      setAdminCoachGroupList(null);
      setHomeLoading(true);
      homePayloadRef.current = null;
    };
    window.addEventListener("smarttt-club-changed", onClubChanged);
    return () => window.removeEventListener("smarttt-club-changed", onClubChanged);
  }, []);

  useEffect(() => {
    if (refreshBrand) {
      refreshBrand();
    }
  }, [refreshBrand, displayUser?.club_id, displayUser?.club?.club_id, displayUser?.club_details?.club_id]);

  const userRole = displayUser.type_user_id;
  const availableRoles = useMemo(
    () => getHomeRolesFromUser(displayUser),
    [displayUser]
  );

  const [activeHomeRole, setActiveHomeRole] = useState(() => {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return readActiveHomeRole(u);
  });
  const [displayedHomeRole, setDisplayedHomeRole] = useState(activeHomeRole);

  useEffect(() => {
    setActiveHomeRole((prev) => {
      const roles = getHomeRoleSwitcherOptions(displayUser);
      if (!roles.length) return prev;
      if (roles.includes(prev)) return prev;
      const next = readActiveHomeRole(displayUser);
      localStorage.setItem(HOME_ACTIVE_ROLE_KEY, next);
      if (isPreviewHomeRole(displayUser, next)) {
        localStorage.setItem(HOME_ROLE_PREVIEW_KEY, "1");
      } else {
        localStorage.removeItem(HOME_ROLE_PREVIEW_KEY);
      }
      return next;
    });
  }, [displayUser]);

  useEffect(() => {
    const targetLang = preferredAppLanguage(displayUser, i18n);
    if (normalizeAppLang(i18n.language) !== targetLang) {
      applyAppLanguage(i18n, targetLang);
    }
  }, [displayUser?.user_lang, i18n]);

  const currentLang = normalizeAppLang(i18n.language);
  const getObjectiveLabel = useCallback(
    (goal) => {
      if (!goal || typeof goal !== "object") return "";
      if (goal.custom_text && String(goal.custom_text).trim()) {
        return String(goal.custom_text).trim();
      }
      const langMap = {
        en: ["translated_name_en", "attribute_name_en", "name_en"],
        fr: ["translated_name_fr", "attribute_name_fr", "name_fr"],
        de: ["translated_name_de", "attribute_name_de", "name_de"],
        es: ["translated_name_es", "attribute_name_es", "name_es"],
        nl: ["translated_name_nl", "attribute_name_nl", "name_nl"],
        pt: ["translated_name_pt", "attribute_name_pt", "name_pt"],
      };
      const preferredKeys = langMap[currentLang] || langMap.en;
      const fallbackKeys = [
        "translated_name",
        "attribute_name",
        "name",
        "translated_name_en",
        "attribute_name_en",
        "name_en",
        "translated_name_fr",
        "attribute_name_fr",
        "name_fr",
        "translated_name_de",
        "attribute_name_de",
        "name_de",
        "translated_name_es",
        "attribute_name_es",
        "name_es",
        "translated_name_nl",
        "attribute_name_nl",
        "name_nl",
        "translated_name_pt",
        "attribute_name_pt",
        "name_pt",
      ];
      for (const key of preferredKeys) {
        if (goal[key]) return goal[key];
      }
      for (const key of fallbackKeys) {
        if (goal[key]) return goal[key];
      }
      return "";
    },
    [currentLang]
  );

  const isAdminView =
    displayedHomeRole === "admin" || displayedHomeRole === "moderator";
  const isCoachView =
    displayedHomeRole === "coach" || displayedHomeRole === "monitor";
  const isPlayerView = displayedHomeRole === "player";
  const hasCoachRole =
    availableRoles.includes("coach") || availableRoles.includes("monitor");
  const { permissions } = usePermissions();
  useCoachLiveTrainingAutoEnter({
    enabled: isCoachView && !tourDemo && isActive,
    user: displayUser,
    permissions,
  });
  const clubId =
    displayUser?.club_id ||
    displayUser?.club?.club_id ||
    displayUser?.club_details?.club_id;
  const hasHomeData = (mdata !== undefined && grplist !== undefined) || tourDemo;

  useEffect(() => {
    if (!isActive || !isPlayerView) return undefined;
    if (!tourSettled) return undefined;
    if (brand?.show_ai_coach === false) return undefined;
    if (clubJoinPromptOpenedRef.current) return undefined;
    const userId = displayUser?.user_id;
    if (!userId || !localStorage.getItem("token")) return undefined;
    if (hasDigipingAutoPrompted("clubJoin", userId)) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const profile = await fetchSmartProfile(null, currentLang);
        if (cancelled) return;
        if (!profile?.clubJoinReview?.pending) return;

        const purposeId = Number(profile?.slots?.S?.purposeId);
        const skipTechnical = purposeId === 1 || purposeId === 2;
        const technicalOpen =
          !skipTechnical &&
          !profile?.isCompetencyblocDone &&
          !profile?.profilingProgress?.complete;
        if (technicalOpen) return;

        clubJoinPromptOpenedRef.current = true;
        markDigipingAutoPrompted("clubJoin", userId);
        const clubName = profile.clubJoinReview.clubName || "";
        openDigitalTwin({
          home_role: "player",
          seedMessage: t("digipingClubJoinHomeSeed", { club: clubName }),
          seedVisible: false,
          profilingIntro: "clubJoin",
          seedKey: "digipingClubJoinHomeSeed",
          seedParams: { club: clubName },
        });
      } catch (err) {
        console.warn("Club join SMART review prompt failed:", err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isActive,
    isPlayerView,
    brand?.show_ai_coach,
    displayUser?.user_id,
    currentLang,
    t,
    tourSettled,
  ]);

  useEffect(() => {
    if (!isActive || !isAdminView) return undefined;
    if (!tourSettled) return undefined;
    if (brand?.show_ai_coach === false) return undefined;
    if (clubSetupPromptOpenedRef.current) return undefined;
    const userId = displayUser?.user_id;
    if (!userId || !localStorage.getItem("token")) return undefined;
    if (!clubId || !hasHomeData) return undefined;
    if (hasDigipingAutoPrompted("clubSetup", userId)) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const status = await clubNeedsQuickSetup({ clubId, groups: grplist });
        if (cancelled || !status.needed) return;
        clubSetupPromptOpenedRef.current = true;
        markDigipingAutoPrompted("clubSetup", userId);
        openClubSetupTwin({
          seedMessage: t("digipingClubSetupHomeSeed", {
            rooms: status.hasRooms ? "yes" : "none",
            groups: status.hasGroups ? "yes" : "none",
            lng: currentAppLanguage(i18n),
          }),
          seedKey: "digipingClubSetupHomeSeed",
          seedParams: {
            rooms: status.hasRooms ? "yes" : "none",
            groups: status.hasGroups ? "yes" : "none",
          },
        });
      } catch (err) {
        console.warn("Club setup prompt failed:", err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isActive,
    isAdminView,
    brand?.show_ai_coach,
    displayUser?.user_id,
    clubId,
    hasHomeData,
    grplist,
    i18n,
    t,
    tourSettled,
  ]);

  // SMART goals / training-axes / technical profiling are opt-in: profile page,
  // explicit Ask Pingflo, or Digiping when it needs a deeper player analysis.
  // Do not auto-open Pingflo on home when switching to the player role.

  const showCoachMultiGroupSessions =
    isCoachView || (isAdminView && hasCoachRole);

  const filledGroups = useMemo(() => {
    if (Array.isArray(grplist) && grplist.length > 0) return grplist;
    if (tourDemo) return demoHomeGroups();
    return grplist;
  }, [grplist, tourDemo]);

  useEffect(() => {
    if (tourDemo) return;
    const id = selectedgrp?.groupDetails?.gr_id || selectedgrp?.gr_id;
    if (isTourDemoId(id)) setselectedgrp(undefined);
  }, [tourDemo, selectedgrp]);
  const filledSelected = useMemo(() => {
    if (selectedgrp && homeGroupRowId(selectedgrp)) return selectedgrp;
    if (tourDemo && Array.isArray(filledGroups) && filledGroups.length) return filledGroups[0];
    return selectedgrp;
  }, [selectedgrp, tourDemo, filledGroups]);
  const filledMetrics = useMemo(() => {
    if (
      mdata &&
      (mdata.totalUsers ||
        mdata.totalGroups ||
        mdata.groups ||
        mdata.totalCoaches ||
        mdata.coachGroupsCount != null ||
        mdata.playerGroupsCount != null ||
        mdata.sessionsPerformedThisYear != null ||
        mdata.sessionsFinalizedThisYear != null ||
        mdata.sessionsScheduledUpcoming != null)
    ) {
      return mdata;
    }
    if (tourDemo) return demoDashboardCounts();
    return mdata;
  }, [mdata, tourDemo]);

  const coachGroupsForSessionList = useMemo(() => {
    if (isCoachView) return filledGroups ?? EMPTY_HOME_GROUP_LIST;
    if (isAdminView && hasCoachRole) {
      return adminCoachGroupList ?? EMPTY_HOME_GROUP_LIST;
    }
    return EMPTY_HOME_GROUP_LIST;
  }, [isCoachView, isAdminView, hasCoachRole, filledGroups, adminCoachGroupList]);

  const coachGroupsListReadyForSessions = useMemo(() => {
    if (!showCoachMultiGroupSessions) return true;
    if (isCoachView) return Array.isArray(grplist) || tourDemo;
    return adminCoachGroupList !== null;
  }, [
    showCoachMultiGroupSessions,
    isCoachView,
    grplist,
    tourDemo,
    adminCoachGroupList,
  ]);

  /** Groups offered when adding a session (coach/admin): coach-assigned list if present, else club list. */
  const addSessionGroupOptions = useMemo(() => {
    const coachList = coachGroupsForSessionList;
    if (Array.isArray(coachList) && coachList.length > 0) return coachList;
    return Array.isArray(filledGroups) ? filledGroups : [];
  }, [coachGroupsForSessionList, filledGroups]);

  const calendarGroupIds = useMemo(() => {
    const rows =
      Array.isArray(addSessionGroupOptions) && addSessionGroupOptions.length > 0
        ? addSessionGroupOptions
        : Array.isArray(filledGroups)
          ? filledGroups
          : [];
    return [...new Set(rows.map(homeGroupRowId).filter(Boolean))];
  }, [addSessionGroupOptions, filledGroups]);

  const handleSessionAddGroupChosen = useCallback((groupRow) => {
    setselectedgrp(groupRow);
    const id = groupRow?.groupDetails?.gr_id || groupRow?.gr_id;
    if (isTourDemoId(id)) return;
    localStorage.setItem("grpinf", JSON.stringify(groupRow));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const user = displayUserRef.current || {};
    const mappedUserType = mapHomeRoleToUserType(activeHomeRole, user);
    const params = {
      user_type: Number(mappedUserType),
      club_id: clubId,
      home_role: activeHomeRole,
    };

    setDisplayedHomeRole(activeHomeRole);
    setHomeLoading(true);

    (async () => {
      try {
        const [adminRes, metricRes] = await Promise.all([
          homepageadmin(params),
          homemetric(params),
        ]);
        if (cancelled) return;

        const payload = adminRes?.data?.data || {};
        homePayloadRef.current = payload;
        const list = payload.groupList || [];
        // Player homePage can omit groups; keep the last loaded / stored list so
        // today's session cards can still query listTS and open Live Training.
        if (list.length > 0 || activeHomeRole !== "player") {
          setgrplist(list);
          const pick = selectHomeGroupFromPayload(list, selectedgrpRef.current);
          setselectedgrp(pick.selected);
          if (pick.persist && pick.selected) {
            localStorage.setItem("grpinf", JSON.stringify(pick.selected));
          }
        } else {
          const stored = readStoredGroupFromLocalStorage();
          if (stored && homeGroupRowId(stored)) {
            setgrplist([stored]);
            setselectedgrp(stored);
          }
        }
        setmdata(metricRes?.data?.data?.dashboardCounts);

        if (roleChangePendingRef.current) {
          const today = getDefaultCalendarDate();
          setSelectedDate(today);
          setFinalizedPickDate(today);
          setSelectionRange(today);
          roleChangePendingRef.current = false;
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (!cancelled) setHomeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeHomeRole, clubId]);

  useEffect(() => {
    if (!((activeHomeRole === "admin" || activeHomeRole === "moderator") && hasCoachRole)) {
      if (displayedHomeRole !== "admin" && displayedHomeRole !== "moderator") {
        setAdminCoachGroupList(null);
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await homepageadmin({
          user_type: 2,
          club_id: clubId,
          home_role: "coach",
        });
        if (!cancelled) {
          setAdminCoachGroupList(response.data?.data?.groupList || []);
        }
      } catch {
        if (!cancelled) setAdminCoachGroupList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeHomeRole, displayedHomeRole, clubId, hasCoachRole]);

  useEffect(() => {
    const focusedGroup = location.state?.focusGroup;
    const focusedSession = location.state?.focusSession;
    if (focusedGroup?.groupDetails?.gr_id) {
      setselectedgrp(focusedGroup);
      localStorage.setItem("grpinf", JSON.stringify(focusedGroup));
      window.dispatchEvent(new CustomEvent("groupSelected"));
    }
    if (focusedSession?.ts_id) {
      localStorage.setItem("rsessn", JSON.stringify(focusedSession));
      window.dispatchEvent(new CustomEvent("sessionSelected"));
      if (focusedSession.ts_date) {
        const sessionDate = moment(focusedSession.ts_date).toDate();
        setSelectedDate(sessionDate);
        setSelectionRange(sessionDate);
      }
    }
  }, [location.state]);

  useEffect(() => {
    const raw = JSON.parse(localStorage.getItem("user") || "{}");
    if (!getHomeRolesFromUser(raw).includes("player")) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await profile({
          club_id: raw.club_id || raw.club_details?.club_id || raw.club?.club_id,
        });
        const u = res?.data?.data;
        if (!cancelled && u) {
          const merged = mergeProfilePreservingActiveClub(raw, u);
          localStorage.setItem("user", JSON.stringify(merged));
          setDisplayUser(merged);
        }
      } catch (_) {
        /* keep cached user */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const showPlayerAutoeval =
    isPlayerView &&
    displayUser &&
    shouldFetchPendingAutoevaluations(displayUser);

  const welcomeName =
    displayUser?.user_alias != null && displayUser.user_alias !== ""
      ? displayUser.user_alias
      : displayUser?.user_first_name || "";
  const showHomeOverlay = homeLoading && isActive && !tourDemo;
  const homeNotifCount = getUserNotificationCount(displayUser);
  const welcomeClubLogo =
    resolveClubImageUrl(displayUser?.club_details?.club_image) || defaultAppLogo;
  const canSwitchClub =
    Number(displayUser?.type_user_id) === 1 ||
    Number(displayUser?.type_user_id) === 2 ||
    Number(displayUser?.type_user_id) === 3 ||
    Number(displayUser?.type_user_id) === 6 ||
    displayUser?.hasAdminRole ||
    displayUser?.hasCommitteeRole ||
    displayUser?.hasCoachRole ||
    displayUser?.hasModeratorRole;

  return (
    <>
      <div id="adminRoot" className="home-theme-root">
        <div className="home-hero-wash" aria-hidden="true" />
        {homeNotifCount > 0 ? (
          <Header
            userRole={userRole}
            user={displayUser}
            viewAsRole={activeHomeRole}
            className="home-header--compact"
            hideClubLogo
            hideRoleBadge
          />
        ) : null}

        <section
          className="home-welcome-card"
          aria-label={t("welcome")}
        >
          <div className="home-welcome-card__copy">
            <p className="home-welcome-card__eyebrow">{t("welcome")}</p>
            <h2 className="home-welcome-card__name">{welcomeName}</h2>
          </div>
          <div className="home-welcome-card__actions header-brand-stack">
            <HeaderRoleBadge user={displayUser} viewAsRole={activeHomeRole} />
            <img
              src={welcomeClubLogo}
              className="clublogo"
              alt=""
              onClick={() => {
                if (canSwitchClub) openSwitchClubDialog();
              }}
              title={t("switchClubPrompt", "Switch club")}
              style={{ cursor: canSwitchClub ? "pointer" : "default" }}
              onError={(e) => {
                const img = e.currentTarget;
                if (img && img.src !== defaultAppLogo) {
                  img.src = defaultAppLogo;
                }
              }}
            />
          </div>
        </section>

        {isPlayerView ? (
          <EmailVerificationBanner
            user={displayUser}
            onVerified={() => {
              try {
                const raw = localStorage.getItem("user");
                if (raw) {
                  /* parent re-reads via displayUser from state in some paths */
                }
              } catch {
                /* ignore */
              }
              window.location.reload();
            }}
          />
        ) : null}

        <AutoevalBanner />
        <NewsletterHomeCard />
        {showPlayerAutoeval && (
          <div
            style={{
              textAlign: "center",
              padding: "4px 16px 12px",
            }}
          >
            <button
              type="button"
              onClick={() => navigate("/player-autoevaluation")}
              style={{
                background: "none",
                border: "none",
                color: "var(--brand-primary, #0877BC)",
                fontSize: "14px",
                fontWeight: 600,
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {t("ae_title")}
            </button>
          </div>
        )}

        <div
          className={[
            "home-dashboard-body",
            showHomeOverlay ? "is-loading" : "",
            showHomeOverlay && hasHomeData ? "has-stale-content" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-busy={showHomeOverlay}
        >
          {showHomeOverlay ? (
            <div
              className="home-dashboard-body__overlay"
              role="status"
              aria-live="polite"
            >
              <span className="smarttt-page-loader__spinner" aria-hidden="true" />
            </div>
          ) : null}
          <div className="home-dashboard-body__content">
        {isPlayerView ? (
          <>
            <MetricsPlayerYear
              key={`player-metrics-${clubId || "none"}`}
              data={filledMetrics}
            />
            {filledGroups && filledGroups.length > 0 ? (
              <div data-tour-id="player-groups">
                <SessionOptions
                  key={`player-groups-${clubId || "none"}`}
                  slideImages={filledGroups}
                  selectedgrp={filledSelected}
                  setselectedgrp={setselectedgrp}
                  availableGroups={filledGroups}
                  onPlayerNewSession={() => setPlayerSessionOpen(true)}
                />
              </div>
            ) : (
              <button
                type="button"
                className="home-player-new-session"
                data-tour-id="player-new-session"
                onClick={() => setPlayerSessionOpen(true)}
              >
                {t("playerNewTrainingSession")}
              </button>
            )}
            <TodaySessions
              playerHomeMode
              groups={filledGroups}
              selectedgrp={filledSelected}
              onSessionClick={(session) => {
                if (session.ts_date) {
                  const sessionDate = moment(session.ts_date).toDate();
                  setFinalizedPickDate(sessionDate);
                  setSelectionRange(sessionDate);
                  setFinalizedCalendarExpanded(!isCalendarToday(sessionDate));
                }
              }}
            />
            <PlayerNewTrainingSessionDialog
              open={playerSessionOpen}
              onClose={() => setPlayerSessionOpen(false)}
              grId={
                filledSelected?.groupDetails?.gr_id ||
                filledSelected?.gr_id ||
                filledGroups?.[0]?.groupDetails?.gr_id ||
                filledGroups?.[0]?.gr_id
              }
              clubId={
                clubId ||
                displayUser?.club_id ||
                displayUser?.club?.club_id ||
                displayUser?.club_details?.club_id
              }
              variant="home"
              onStarted={(session) => {
                const startMs = session?.starts_at
                  ? new Date(session.starts_at).getTime()
                  : Date.now();
                const liveNow = startMs <= Date.now() + 15 * 60 * 1000;
                const dayKey = session?.ts_date
                  ? String(session.ts_date).slice(0, 10)
                  : session?.starts_at
                    ? moment(session.starts_at).format("YYYY-MM-DD")
                    : null;
                if (dayKey && /^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
                  const sessionDay = moment(dayKey, "YYYY-MM-DD").toDate();
                  setFinalizedPickDate(sessionDay);
                  setSelectionRange(sessionDay);
                }
                if (liveNow) {
                  persistLiveTrainingSession(session, { gr_id: session.gr_id });
                  requestLiveFullscreen();
                  navigate("/live-training");
                }
              }}
            />
            {displayUser?.userFocuses &&
              (displayUser.userFocuses.primary ||
                (displayUser.userFocuses.secondary &&
                  displayUser.userFocuses.secondary.length > 0)) && (
                <section
                  className="home-player-goals"
                  aria-label={t("trainingGoals") || "Training goals"}
                >
                  <h3 className="home-player-goals__title">
                    {t("yourTrainingGoals") || "Your training goals"}
                  </h3>
                  <div className="home-player-goals__grid">
                    {displayUser.userFocuses.primary && (
                      <div className="home-player-goals__card home-player-goals__card--primary">
                        <span className="home-player-goals__badge">
                          {t("primaryFocus") || "Main goal"}
                        </span>
                        <p className="home-player-goals__value">
                          {getObjectiveLabel(displayUser.userFocuses.primary)}
                        </p>
                      </div>
                    )}
                    {displayUser.userFocuses.secondary &&
                      displayUser.userFocuses.secondary.length > 0 && (
                        <div className="home-player-goals__card home-player-goals__card--secondary">
                          <span className="home-player-goals__badge">
                            {t("secondaryFocuses") || "Also working on"}
                          </span>
                          <ul className="home-player-goals__list">
                            {displayUser.userFocuses.secondary.map((s, i) => (
                              <li key={s.attribute_id || i}>{getObjectiveLabel(s)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </div>
                </section>
              )}
            <PlayerLastSessionCard />
            <HomeCalendarSessionsSection
              selectedDate={finalizedPickDate}
              onDateChange={(date) => {
                setFinalizedPickDate(date);
                setSelectionRange(date);
              }}
              groupIds={calendarGroupIds}
              tourAgenda={tourDemo}
              calendarExpanded={finalizedCalendarExpanded}
              onCalendarExpandedChange={setFinalizedCalendarExpanded}
              calendarClassName="home-player-finalized-calendar"
              calendarTourId="player-calendar"
              sessionsProps={{
                coachHomeGroups: filledGroups ?? [],
                aggregateCoachSessionsAcrossGroupsByDate: true,
                coachGroupsListReady: Array.isArray(filledGroups),
                playerHomeMode: true,
              }}
            />
          </>
        ) : isCoachView ? (
          <>
            <MetricsCoachHome key={`coach-metrics-${clubId || "none"}`} data={filledMetrics} />
            <div data-tour-id="coach-groups">
            <SessionOptions
              key={`coach-groups-${clubId || "none"}`}
              slideImages={filledGroups}
              selectedgrp={filledSelected}
              setselectedgrp={setselectedgrp}
              availableGroups={addSessionGroupOptions}
            />
            </div>
            <TodaySessions
              groups={filledGroups}
              selectedgrp={filledSelected}
              onSessionClick={(session) => {
                if (session.ts_date) {
                  const sessionDate = moment(session.ts_date).toDate();
                  setSelectedDate(sessionDate);
                  setSelectionRange(sessionDate);
                  setCalendarExpanded(!isCalendarToday(sessionDate));
                }
              }}
            />
            <HomeCalendarSessionsSection
              selectedDate={selectedDate}
              onDateChange={(date) => {
                setSelectedDate(date);
                setSelectionRange(date);
              }}
              groupIds={calendarGroupIds}
              calendarExpanded={calendarExpanded}
              onCalendarExpandedChange={setCalendarExpanded}
              sessionsKey={`home-sessions-${clubId || "none"}`}
              sessionsProps={{
                selectedgrp: filledSelected,
                coachHomeGroups: coachGroupsForSessionList,
                aggregateCoachSessionsAcrossGroupsByDate:
                  showCoachMultiGroupSessions,
                coachGroupsListReady: coachGroupsListReadyForSessions,
                addSessionGroupOptions,
                onSessionAddGroupChosen: handleSessionAddGroupChosen,
              }}
            />
          </>
        ) : isAdminView ? (
          <>
            <MetricsAdminHome
              key={`admin-metrics-${clubId || "none"}`}
              data={filledMetrics}
            />
            <div data-tour-id="admin-groups">
              <SessionOptions
                key={`admin-groups-${clubId || "none"}`}
                slideImages={filledGroups}
                selectedgrp={filledSelected}
                setselectedgrp={setselectedgrp}
                availableGroups={addSessionGroupOptions}
              />
            </div>
            <TodaySessions
              groups={filledGroups}
              selectedgrp={filledSelected}
              onSessionClick={(session) => {
                if (session.ts_date) {
                  const sessionDate = moment(session.ts_date).toDate();
                  setSelectedDate(sessionDate);
                  setSelectionRange(sessionDate);
                  setCalendarExpanded(!isCalendarToday(sessionDate));
                }
              }}
            />
            <HomeCalendarSessionsSection
              selectedDate={selectedDate}
              onDateChange={(date) => {
                setSelectedDate(date);
                setSelectionRange(date);
              }}
              groupIds={calendarGroupIds}
              calendarExpanded={calendarExpanded}
              onCalendarExpandedChange={setCalendarExpanded}
              sessionsKey={`home-sessions-${clubId || "none"}`}
              sessionsProps={{
                selectedgrp: filledSelected,
                coachHomeGroups: coachGroupsForSessionList,
                aggregateCoachSessionsAcrossGroupsByDate:
                  showCoachMultiGroupSessions,
                coachGroupsListReady: coachGroupsListReadyForSessions,
                addSessionGroupOptions,
                onSessionAddGroupChosen: handleSessionAddGroupChosen,
              }}
            />
          </>
        ) : (
          <>
            <Metricsp data={filledMetrics} />
            <SessionOptions
              slideImages={filledGroups}
              selectedgrp={filledSelected}
              setselectedgrp={setselectedgrp}
              availableGroups={filledGroups}
            />
            <TodaySessions
              groups={filledGroups}
              selectedgrp={filledSelected}
              onSessionClick={(session) => {
                if (session.ts_date) {
                  const sessionDate = moment(session.ts_date).toDate();
                  setSelectedDate(sessionDate);
                  setSelectionRange(sessionDate);
                  setCalendarExpanded(!isCalendarToday(sessionDate));
                }
              }}
            />
            <HomeCalendarSessionsSection
              selectedDate={selectedDate}
              onDateChange={(date) => {
                setSelectedDate(date);
                setSelectionRange(date);
              }}
              groupIds={calendarGroupIds}
              calendarExpanded={calendarExpanded}
              onCalendarExpandedChange={setCalendarExpanded}
              sessionsProps={{
                selectedgrp: filledSelected,
                playerHomeMode: true,
                coachHomeGroups: coachGroupsForSessionList,
                aggregateCoachSessionsAcrossGroupsByDate: false,
                coachGroupsListReady: coachGroupsListReadyForSessions,
                addSessionGroupOptions: [],
                onSessionAddGroupChosen: handleSessionAddGroupChosen,
              }}
            />
          </>
        )}
          </div>
        </div>
      </div>
      <LiveTrainingReturnFab />
    </>
  );
}
